import { formatMoney } from "@/lib/campaigns";

/**
 * "Pay with your existing credits" — the all-or-nothing opt-in, sitting directly under the figure
 * it reduces (Judd, 2026-08-26).
 *
 * A REAL OPT-IN, not a display toggle: existing credits are used only if the client chooses to use
 * them. Unticked, the launch buys the campaign's full requirement on the card and the wallet
 * balance is left where it is — which some clients want, to hold credit for other gifting.
 *
 * The right-hand figure carries the whole result, which is why there is no separate "credits
 * applied" line: ticked it reads what came off, unticked it reads what is sitting there unused.
 *
 * The balance is what this campaign may actually draw on — credits that outlive it, less the
 * headroom the group's other campaigns already hold — not the raw wallet total.
 *
 * WHICH MEANS A FULL WALLET CAN OFFER NOTHING, and the row used to answer that by disappearing. A
 * client looking at 2,500 credits on their wallet screen then found no way to spend them here and
 * no statement that there was a reason — the correct refusal read as a missing feature. Three
 * states now, not two: spendable credits, a wallet whose credits are all spoken for, and no wallet
 * at all, which is still the clean checkout the new-user state wants.
 */
export interface ApplyCreditsRowProps {
  /** What THIS campaign may draw on, 1 credit == 1 USD. Zero does not mean an empty wallet. */
  balance: number;
  /**
   * What the wallet holds, before the other campaigns' headroom comes off.
   *
   * Only ever used to explain a zero `balance`. It is also 0 when the reader lacks `view CREDIT` —
   * the summary query is disabled and there is no payload — which is what keeps a message about a
   * wallet off the screen of somebody who cannot see one.
   */
  walletBalance?: number;
  /** What this order takes from it. Capped at the deposit while pay-as-you-give is on. */
  applied: number;
  checked: boolean;
  onChange: (next: boolean) => void;
}

export default function ApplyCreditsRow({
  balance,
  walletBalance = 0,
  applied,
  checked,
  onChange,
}: ApplyCreditsRowProps) {
  // Nothing to draw on, but credits in the wallet: say why, rather than leaving a client to
  // conclude the feature is missing. No checkbox — there is genuinely nothing to opt into.
  if (balance <= 0 && walletBalance > 0) {
    return (
      <p className="m-0 py-1.5 font-lato text-[12.5px] leading-[1.4] text-graphite">
        Your {formatMoney(walletBalance)} in wallet credits is reserved by your other campaigns and
        cannot fund this one.
      </p>
    );
  }
  // No wallet at all — or a reader who may not see one. The clean checkout stays clean.
  if (balance <= 0) return null;

  return (
    <label className="flex cursor-pointer items-center gap-2.5 py-1.5 font-lato text-[13px] text-rodeo-black">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 shrink-0 cursor-pointer gfg-checkbox"
      />
      <span className="flex-1 leading-[1.35]">Pay with your existing credits</span>
      <span className="shrink-0 font-bold whitespace-nowrap text-dust-ink">
        {checked ? `${formatMoney(applied)} credits applied` : `${formatMoney(balance)} available`}
      </span>
    </label>
  );
}
